import { RequestOptions } from '../services/api'
import { ICompany } from './company'
import { IJobRole } from './job-role'
import { IUser } from './user'

export enum ProfileStatus {
  STARTED = 'started',
  PROCCESSING = 'proccessing',
  CAREER_PATH_CREATED = 'career-path-created',
  COMPLETED = 'completed',
}

export interface IPhoneNumber {
  countryCode: string
  number: string
}

export enum IdentificationDocumentType {
  Passport = 'passport',
  IdentityCard = 'identification',
  ForeignIdentityCard = 'foreign_identity_card',
  DrivingLicense = 'driving_license',
  ProofOfAddress = 'proof_of_address',
  Payslip = 'payslip',
}

export interface IProfileDocument {
  type: IdentificationDocumentType
  number: string
  expirationDate?: Date
  issueDate?: Date
  issuingCountry?: string
  issuingCity?: string
  issuingAuthority?: string
  frontImageUrl?: string
  backImageUrl?: string
}

export interface IAddress {
  line1: string
  line2: string
  city: string
  state: string
  /** The ISO 3166-1 alpha-2 country code */
  countryCode: string
  postalCode: string
}

interface IWorkExperience {
  position: string
  monthsOfExperience: number
  company: string
}

interface IEducation {
  educationProviderName: string
  courseName: string
}

export enum CareerFeling {
  RESKILLING = 'reskilling',
  UPSLILLING = 'upskilling',
}

export enum AIReadinessLevel {
  Beginner = 'beginner', // Learning what AI tools can do, trying prompts, little structure.
  UpperBeginner = 'upper_beginner', // Use AI tools frequently (ChatGPT, Claude, Notion AI, etc.) for basic tasks but lack workflow design or system thinking.
  Intermediate = 'intermediate', // Confident using multiple AI tools together (Zapier, n8n, Lidy, Notion automations). Build agent-assisted workflows, but don’t touch APIs or infrastructure.
  Advanced = 'advanced', // Design and build complete agent systems, APIs, orchestration layers, or AI products end-to-end. Deep technical understanding.
}

export enum TeamTechnicalLevel {
  NonTechnical = 'non_technical', // Not technical - We prefer straightforward, button-click solutions
  Mixed = 'mixed', // A mix - Some technical skills, some not
  Technical = 'technical', // Very technical - Comfortable with coding, APIs, and advanced setups
}

export enum CompanySize {
  Startup = 'startup', // Startup / Small team - under 50 employees
  Growth = 'growth', // Growth stage - 50-200 employees
  Enterprise = 'enterprise', // Enterprise - 200+ employees
}

export enum AIPrimaryUseCase {
  Content = 'content', // Content & Marketing
  Sales = 'sales', // Sales & Customer Management
  Operations = 'operations', // Operations & Data
  Finance = 'finance', // Finance & Reporting
  Support = 'support', // Customer Support
}

export enum AIModelPreference {
  GPT = 'gpt', // GPT (OpenAI)
  Claude = 'claude', // Claude (Anthropic)
  Gemini = 'gemini', // Gemini (Google)
  LLaMA = 'llama', // LLaMA / Meta
  Mistral = 'mistral', // Mistral
  OpenSource = 'open_source', // Open-Source Models
  FineTuned = 'fine_tuned', // Fine-Tuned Models
  Custom = 'custom', // Custom / In-house
  NoPreference = 'no_preference', // No Preferences
}

export enum AutomationExperience {
  ManualProcesses = 'manual_processes', // Manual processes - We mostly do things by hand
  BasicAutomation = 'basic_automation', // Basic automation - We use simple tools like Zapier or IFTTT
  AdvancedWorkflows = 'advanced_workflows', // Advanced workflows - We use complex automations and integrations
}

export interface IProfile {
  _id?: string
  user: string | IUser
  linkedInUrl?: string
  companyName: string
  currentRole: IJobRole | string
  yearsOfExperience: number
  location: string
  bio?: string
  skills: string[]
  education: IEducation[]
  workExperiences: IWorkExperience[]
  suggestedSkills: string[]
  careerFeeling: CareerFeling
  weeklyLearningTime: WeeklyLearningTime
  careerPathPreference: CareerPathPreference
  upskillingReason: UpskillingReason
  status: ProfileStatus
  careerPathways?: [
    {
      _id: string
      roleName: string
    },
  ]
  documents: IProfileDocument[]
  address?: IAddress
  phoneNumbers?: IPhoneNumber[]
  professionals?: IProfessional[]
  /** @deprecated use `learningBudgetBalance` instead */
  learningBudget?: number
  learningBudgetBalance?: number
  learningBudgetGrossBalance?: number
  financialData?: IProfileFinancialData | null
  companySize: CompanySize | null
  teamTechnicalLevel: TeamTechnicalLevel | null
  automationExperience: AutomationExperience | null
  aiReadiness: AIReadinessLevel | null
  aiPrimaryUseCase: AIPrimaryUseCase | null
  aiModelPreferences: AIModelPreference[] | null
}

export type IProfileWithFinancialData = Omit<IProfile, 'financialData'> &
  Required<Pick<IProfile, 'financialData'>>

export interface IGoal {
  goal: string
  description: string
}

export interface IAreasFormImprovement {
  area: string
  skillsToImprove: string[]
}

export interface IDevelopmentActionPlan {
  actions: {
    title: string
    summary: string
    action: string
  }[]
}

export interface IUserProfileCareerPath {
  roleName: string
  summary: string
  shortTermGoals: IGoal[]
  longTermGoals: IGoal[]
  areasForImprovement: IAreasFormImprovement[]
  developmentActionPlan: IDevelopmentActionPlan
}

export enum UpskillingReason {
  BUILDING_A_SUPPORT_NETWORK = 'BUILDING_A_SUPPORT_NETWORK',
  DEEP_DIVING_INTO_A_SKILL_AT_MY_OWN_PACE = 'DEEP_DIVING_INTO_A_SKILL_AT_MY_OWN_PACE',
  INTERACTING_WITH_EXPERTS_MY_AREA = 'INTERACTING_WITH_EXPERTS_MY_AREA',
  GETTING_AN_OFFICIAL_CERTIFICATE_FOR_A_SKILL = 'GETTING_AN_OFFICIAL_CERTIFICATE_FOR_A_SKILL',
  TO_REMAIN_COMPETITIVE_IN_THE_JOB_MARKET = 'TO_REMAIN_COMPETITIVE_IN_THE_JOB_MARKET',
}

export enum CareerPathPreference {
  // TODO more options to be added
  MOVING_INTO_A_LEADERSHIP_ROLE = 'MOVING_INTO_A_LEADERSHIP_ROLE',
}

export enum WeeklyLearningTime {
  LESS_THAN_1_HOUR = 'LESS_THAN_1_HOUR',
  BETWEEN_1_AND_3_HOURS = 'BETWEEN_1_AND_3_HOURS',
  BETWEEN_4_AND_8_HOURS = 'BETWEEN_4_AND_8_HOURS',
  MORE_THAN_8_HOURS = 'MORE_THAN_8_HOURS',
}

export interface IProfessional {
  company: string | ICompany
  currentRole?: string
  contractType: string
  netSalary: number
  grossSalary: number
  currency: string
  startedAt: Date
  isActive?: boolean
}

/**
 * @deprecated used in old profiles, for new data use `DetailedManualFinancialObligations`
 * */
export type ManualFinancialObligationsAsNumber = number

export type DetailedManualFinancialObligations = {
  rentOrMortgage: number
  loanRepayments: number
  utilities: number
}

export interface IProfileFinancialData {
  manualFinancialObligations:
    | ManualFinancialObligationsAsNumber
    | DetailedManualFinancialObligations
  hasEuropeanInvestmentFundSupport: boolean
  europeanInvestmentFundAmount: number
}

// Fields with sensitive data that should not be included in the default profile response
export enum IProfileRestrictedFields {
  FinancialData = 'financialData',
  Documents = 'documents',
}

export type IUserProfilePostPayload = {
  userId: string
  linkedInUrl?: string
  companyName?: string
  currentRole?: string
  location?: string
  bio?: string
  yearsOfExperience?: number
  address?: IAddress
  phoneNumbers?: IPhoneNumber[]
  learningBudget?: number
  financialData?: IProfileFinancialData | null
}

export type IUserProfilePatchPayload = {
  companyName?: string
  currentRole?: string
  location?: string
  bio?: string
  yearsOfExperience?: number
  skills?: string[]
  careerFeeling?: CareerFeling
  weeklyLearningTime?: WeeklyLearningTime
  careerPathPreference?: CareerPathPreference
  upskillingReason?: UpskillingReason
  address?: IAddress
  phoneNumbers?: IPhoneNumber[]
  learningBudget?: number
  financialData?: IProfileFinancialData | null
  companySize?: CompanySize
  teamTechnicalLevel?: TeamTechnicalLevel
  automationExperience?: AutomationExperience
  aiReadiness?: AIReadinessLevel
  aiPrimaryUseCase?: AIPrimaryUseCase
  aiModelPreferences?: AIModelPreference[]
}

export type GetProfileArgs = {
  includeRestrictedField?: IProfileRestrictedFields[]
  options?: RequestOptions
}
